<template>
    <VJoyWrapper class="recommendation-card" direction="column" row-gap="0">
        <VJoyWrapper
            column-gap="5"
            direction="row"
            align="flex-start"
            class="recommendation-card__container"
            no-margin
            :class="{'recommendation-card--hidden': isHidden}"
        >
            <VJoyAvatar
                size="medium"
                class="recommendation-card__avatar"
                :photo-url="recommendation.authorImgUrl"
                :full-name="`${recommendation.authorFirstName} ${recommendation.authorLastName}`"
            />
            <VJoyWrapper align="flex-start" direction="column" row-gap="0" class="recommendation-card__content">
                <VJoyLink v-if="recommendation.authorProfileUrl" preset="primary-450" color="teal" :href="recommendation.authorProfileUrl">
                    {{ recommendation.authorFirstName }} {{ recommendation.authorLastName }}
                </VJoyLink>
                <VJoyText v-else preset="primary-450">{{ recommendation.authorFirstName }} {{ recommendation.authorLastName }}</VJoyText>
                <VJoyWrapper
                    v-if="recommendation.authorCompanyName || recommendation.authorJobTitle || recommendation.authorProfileUrl"
                    class="recommendation-card__content__job-title"
                    no-margin
                    direction="row"
                    column-gap="1"
                >
                    <VJoyText v-if="recommendation.authorJobTitle" preset="primary-300" size="small">
                        {{ recommendation.authorJobTitle }}
                    </VJoyText>
                    <template v-if="recommendation.authorProfileUrl || recommendation.authorCompanyName">
                        <VJoyText v-if="recommendation.authorJobTitle" size="small">-</VJoyText>
                        <template v-if="recommendation.authorProfileUrl">
                            <VJoyIcon color="primary" name="logo-brew" size="xsmall" />
                            <VJoyText preset="primary-300">
                                {{ t('profile.section.recommendation.malt.freelancer.label') }}
                            </VJoyText>
                        </template>
                        <template v-else-if="recommendation.authorCompanyName">
                            <img
                                v-if="isNewCompanyLogoEnabled && recommendation.companyLogoUrl"
                                :src="recommendation.companyLogoUrl"
                                :alt="recommendation.authorCompanyName"
                                width="16"
                                height="16"
                                style="object-fit: contain"
                            />
                            <VJoyIcon v-else name="company-placeholder" size="xsmall" />
                            <VJoyText preset="primary-300">
                                {{ recommendation.authorCompanyName }}
                            </VJoyText>
                        </template>
                    </template>
                </VJoyWrapper>
                <VJoyText class="recommendation-card__content__recommended-on-label" preset="primary-200">
                    {{ t('profile.section.recommendation.recommended.on.label', {0: formattedDate}) }}
                </VJoyText>
                <SeeMoreOrRedirect
                    v-if="recommendation.textContent"
                    :max-lines="4"
                    :locked="{
                        allowHref: !mf,
                        label: t('profile.section.recommendation.private', [recommendation.authorFirstName]),
                        componentName: 'profile-recommendations-section',
                        targetId: anchorsPageProfile.recommendationsSection,
                    }"
                    @see-more-clicked-when-locked="$emit('seeMoreClickedWhenLocked')"
                >
                    <VJoyText class="recommendation-card__content__text" preset="primary-400">
                        {{ recommendation.textContent }}
                    </VJoyText>
                </SeeMoreOrRedirect>
            </VJoyWrapper>
            <VJoyWrapper
                v-if="!mf && isEditionEnabled"
                direction="row"
                justify="center"
                align="center"
                :no-margin="true"
                class="recommendation-card__visibility-container"
            >
                <VJoyButton
                    :icon="visibilityIcon"
                    :loading="updatingVisibility"
                    :label="visibilityLabel"
                    size="small"
                    variant="ghost"
                    data-testid="toggle-recommendation-visibility"
                    @click="toggleVisibility"
                />
            </VJoyWrapper>
        </VJoyWrapper>
        <VJoyHighlight v-if="recommendation.hidden && isOwnRecommendation">
            <VJoyWrapper direction="row" justify="flex-start" no-margin class="recommendation-card__visibility-message">
                <VJoyIcon size="xsmall" name="eye-masked" />
                <VJoyText>{{ t('profile.section.recommendation.hidden.label') }}</VJoyText>
            </VJoyWrapper>
        </VJoyHighlight>
    </VJoyWrapper>
</template>

<script lang="ts" setup>
    import {computed, ref} from 'vue';
    import {useAuth, useProfilePageViewMode, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyAvatar, VJoyButton, VJoyHighlight, VJoyIcon, VJoyLink, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import SeeMoreOrRedirect from '~/components/_common/see-more/SeeMoreOrRedirect.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
    import type {Recommendation} from '~/types/domain/Recommendations';

    // Composables
    const {t} = useTranslation();
    const {isEditionEnabled} = useProfilePageViewMode();
    const isNewCompanyLogoEnabled = useFeatureFlag('new-company-logo');

    // Store
    const {toggleRecommendationVisibility} = useRecommendationsStore();
    const {user} = useAuth();

    // Props
    const props = defineProps<{
        recommendation: Recommendation;
        mf: boolean;
    }>();

    const emits = defineEmits(['seeMoreClickedWhenLocked']);

    // Computed
    const formattedDate = computed(() => {
        if (!props.recommendation.lastUpdateDate) return '';
        return Intl.DateTimeFormat().format(new Date(props.recommendation.lastUpdateDate));
    });
    const updatingVisibility = ref(false);

    const isOwnRecommendation = computed(() => props.recommendation.accountId === user?.id);
    const isHidden = computed(() => props.recommendation.hidden);
    const visibilityIcon = computed(() => (isHidden.value ? 'eye' : 'eye-masked'));
    const visibilityLabel = computed(() => {
        return isHidden.value ? t('profile.section.recommendation.show.label') : t('profile.section.recommendation.hide.label');
    });

    // Methods
    async function toggleVisibility() {
        updatingVisibility.value = true;
        await toggleRecommendationVisibility(props.recommendation);
        updatingVisibility.value = false;
    }
</script>
<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .recommendation-card {
        width: 100%;
        align-items: flex-start;
        flex-wrap: wrap;

        &__content {
            &__job-title {
                margin-top: var(--joy-core-spacing-1);
            }
            &__text {
                margin-top: var(--joy-core-spacing-2);
            }
            &__recommended-on-label {
                margin-top: var(--joy-core-spacing-1);
                color: var(--joy-color-neutral-40);
            }
        }

        &__container {
            width: 100%;
            align-items: flex-start;
            flex-wrap: wrap;
        }

        &--hidden {
            .recommendation-card__avatar,
            .recommendation-card__content {
                opacity: 0.5;
            }
        }

        &__content {
            flex: 1;
            margin: 0;
        }

        &__visibility-container {
            width: 100%;
            order: 3;

            @include joy.screen_sm(min) {
                width: auto;
                order: 0;
                margin-left: auto;
                align-self: center;
            }
        }
    }
</style>
